import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import StatCard from '../components/ui/StatCard'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import AuthGuard from '../components/AuthGuard'
import ProfileCompletionGuard from '../components/ProfileCompletionGuard'
import WakeUpNumberCard from '../components/WakeUpNumberCard'
import { CALL_STATUSES, APP_CONFIG } from '../data/constants.jsx'
import { useIcon } from '../hooks/useIcons.jsx'
import { parseTimeString, formatBengaliTime, parseWaitTime } from '../utils/timeHelpers'
import { getErrorMessage, logError, isRateLimitError, extractRateLimitInfo } from '../utils/errorHelpers'
import apiService from '../services/api'

const WakeUp = () => {
  return (
    <AuthGuard 
      requireAuth={true}
      title="ওয়েক আপ সেবা ব্যবহার করতে লগিন করুন"
      description="ফজরের জন্য জাগানোর সেবা ব্যবহার করতে অ্যাকাউন্টে লগিন করুন।"
    >
      {({ user, logout, openAuth }) => <WakeUpContent user={user} logout={logout} openAuth={openAuth} />}
    </AuthGuard>
  )
}

const WakeUpContent = ({ user, logout, openAuth }) => {
  const navigate = useNavigate()
  const [currentNumber, setCurrentNumber] = useState(null)
  const [trackingId, setTrackingId] = useState(null)
  const [remainingCalls, setRemainingCalls] = useState(0)
  const [maxCalls, setMaxCalls] = useState(5)
  const [canRequestNext, setCanRequestNext] = useState(true)
  const [availableCount, setAvailableCount] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [loading, setLoading] = useState(true)
  const [numberLoading, setNumberLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isLoadingNumber, setIsLoadingNumber] = useState(false)
  const [timeSettings, setTimeSettings] = useState(null)
  const [isServiceActive, setIsServiceActive] = useState(false)
  const [rateLimitWaitTime, setRateLimitWaitTime] = useState(0)
  const [showUpdateButton, setShowUpdateButton] = useState(true)
  const [isRateLimited, setIsRateLimited] = useState(false)
  const [errorReason, setErrorReason] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [region, setRegion] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // Using utility functions for better maintainability
  
  useEffect(() => {
    // Prevent duplicate initialization
    if (isInitialized) return

    const loadWakeUpData = async () => {
      try {
        setLoading(true)
        setIsInitialized(true)

        // Load time settings and basic data
        const [timeSettingsData, availableData, cooldownStatus] = await Promise.all([
          apiService.getWakeUpTimeSettings(),
          apiService.getAvailableMembersNotWokenUp(),
          apiService.getUserCooldownStatus()
        ])

        setTimeSettings(timeSettingsData)
        setIsServiceActive(timeSettingsData.is_active)
        setAvailableCount(availableData)

        // Set the user's actual max daily calls from the API response
        setMaxCalls(cooldownStatus.max_daily_calls)
        setRemainingCalls(cooldownStatus.remaining_daily_calls)

        // Only load phone numbers if service is active AND user is OTP verified
        if (timeSettingsData.is_active && user.otp_verified) {
          await loadInitialNumber()
        }
      } catch (err) {
        console.error('Failed to load wake-up data:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadWakeUpData()
  }, [])
  
  // Load initial number on page load - check for pending first
  const loadInitialNumber = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingNumber) {
      console.log('Already loading number, skipping duplicate call')
      return
    }
    
    try {
      setIsLoadingNumber(true)
      setNumberLoading(true)
      
      // First, check if user has a pending number to update
      const pendingResult = await apiService.getPendingNumber()
      
      if (pendingResult.has_pending) {
        console.log('Found pending number, showing it instead of requesting new one')
        setCurrentNumber(pendingResult.number)
        setTrackingId(pendingResult.tracking_id)
        setRemainingCalls(pendingResult.remaining_calls || 0)
        setCanRequestNext(pendingResult.remaining_calls > 0)
        setShowUpdateButton(pendingResult.show_update_button !== false) // Default to true
        setIsRateLimited(pendingResult.rate_limited || false)
        setError(null)
        return
      }
      
      // No pending number, request a new one silently (no rate limit popup on initial load)
      await requestNewNumberSilent()
    } catch (err) {
      console.error('Failed to load initial number:', err)
      setError(err.message)
    } finally {
      setNumberLoading(false)
      setIsLoadingNumber(false)
    }
  }

  // Request a new number silently (used on initial load - no popup)
  const requestNewNumberSilent = async () => {
    try {
      const result = await apiService.getRateLimitedNumber()
      
      // Check if service is active from the API response
      if (result.hasOwnProperty('is_active') && result.is_active === false) {
        // Service is inactive (outside Fajr hours)
        setCurrentNumber(null)
        setIsServiceActive(false)
        setError(null)
        return
      }
      
      if (result.number) {
        setCurrentNumber(result.number)
        setTrackingId(result.tracking_id)
        setRemainingCalls(result.remaining_calls || 0)
        setCanRequestNext(result.remaining_calls > 0)
        setShowUpdateButton(true) // Reset to show update button for new numbers
        setIsRateLimited(false)
        setError(null)
        // Update service active status if provided
        if (result.hasOwnProperty('is_active')) {
          setIsServiceActive(result.is_active)
        }
      } else {
        setCurrentNumber(null)
        setErrorMessage(result.message || 'কোন নম্বর পাওয়া যায়নি')
        setErrorReason(result.reason || result.reason_detail || 'অনুগ্রহ করে একটু পরে আবার চেষ্টা করুন')
        setRegion(result.region || null)
        setError(null) // Don't show page-level error
        // Update service active status if provided
        if (result.hasOwnProperty('is_active')) {
          setIsServiceActive(result.is_active)
        }
      }
    } catch (err) {
      console.error('Failed to get new number on initial load:', err)
      // On initial load, don't show popup - just set no number state
      setCurrentNumber(null)
      setErrorMessage('কোন নম্বর পাওয়া যায়নি')
      setErrorReason('বর্তমানে কোন নম্বর উপলব্ধ নেই। একটু পরে চেষ্টা করুন।')
      setRegion(null)
      setError(null) // Don't show page-level error for rate limits
    }
  }

  // Request a new number with popup (used by Next Number button)
  const requestNewNumber = async () => {
    try {
      const result = await apiService.getRateLimitedNumber()
      
      // Check if service is active from the API response
      if (result.hasOwnProperty('is_active') && result.is_active === false) {
        // Service is inactive (outside Fajr hours)
        setCurrentNumber(null)
        setIsServiceActive(false)
        setError(null)
        return
      }
      
      if (result.number) {
        setCurrentNumber(result.number)
        setTrackingId(result.tracking_id)
        setRemainingCalls(result.remaining_calls || 0)
        setCanRequestNext(result.remaining_calls > 0)
        setShowUpdateButton(true) // Reset to show update button for new numbers
        setIsRateLimited(false)
        setError(null)
        // Update service active status if provided
        if (result.hasOwnProperty('is_active')) {
          setIsServiceActive(result.is_active)
        }
      } else {
        setCurrentNumber(null)
        setErrorMessage(result.message || 'কোন নম্বর পাওয়া যায়নি')
        setErrorReason(result.reason || result.reason_detail || 'অনুগ্রহ করে একটু পরে আবার চেষ্টা করুন')
        setRegion(result.region || null)
        setError(null) // Don't show page-level error for rate limits
        // Update service active status if provided
        if (result.hasOwnProperty('is_active')) {
          setIsServiceActive(result.is_active)
        }
      }
    } catch (err) {
      console.error('Failed to get new number:', err)
      // Handle rate limiting - show in card instead of popup
      if (err.message.includes('Please wait') || err.message.includes('cooldown')) {
        const waitTime = parseWaitTime(err.message)
        if (waitTime > 0) {
          setRateLimitWaitTime(waitTime)
          setIsRateLimited(true)
          console.log('Rate limit active, showing in card with wait time:', waitTime)
        } else {
          console.log('Cooldown active, maintaining current state')
        }
      } else {
        setError(err.message)
      }
    }
  }

  // Update time settings periodically (every minute)
  useEffect(() => {
    // Don't start periodic updates until initial data is loaded
    if (!isInitialized) return

    const updateTimeSettings = async () => {
      try {
        const timeSettingsData = await apiService.getWakeUpTimeSettings()
        setTimeSettings(timeSettingsData)

        // If service became active, load phone numbers (only if not already loading AND user is OTP verified)
        if (timeSettingsData.is_active && !isServiceActive && !isLoadingNumber && user.otp_verified) {
          await loadInitialNumber()
        }
        // If service became inactive, clear current number
        else if (!timeSettingsData.is_active && isServiceActive) {
          setCurrentNumber(null)
        }

        setIsServiceActive(timeSettingsData.is_active)
      } catch (err) {
        console.error('Failed to update time settings:', err)
      }
    }

    const interval = setInterval(updateTimeSettings, 60000) // Update every minute
    return () => clearInterval(interval)
  }, [isServiceActive, isInitialized])
  
  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    
    return () => clearInterval(timer)
  }, [])
  
  // Handle call status update
  const handleStatusUpdate = async (trackingId, statusId, phoneNumber) => {
    try {
      const result = await apiService.updateCallStatus(trackingId, statusId)
      console.log('Status updated:', statusId, 'for phone:', phoneNumber)

      // Check if user was auto-marked as woken up
      if (result.auto_woke_up) {
        alert(`আপনি আজকে ফজরে জেগেছেন! 🌅 (+${result.wake_up_points_earned} পয়েন্ট)`)
      }

      // Update state based on response
      setCanRequestNext(result.can_request_next)
      setRemainingCalls(result.remaining_daily_calls)

      // Always refresh the available count after any status update
      try {
        const availableData = await apiService.getAvailableMembersNotWokenUp()
        setAvailableCount(availableData)
        console.log('Available count updated after status update')
      } catch (countErr) {
        console.error('Failed to refresh available count:', countErr)
      }
    } catch (err) {
      console.error('Failed to update status:', err)
      setError('Failed to update call status')
    }
  }

  // Handle next number request - always request new number
  const handleRequestNext = async () => {
    setIsLoadingNumber(true)
    setNumberLoading(true)
    try {
      await requestNewNumber()
      
      // Refresh available count after getting new number
      try {
        const availableData = await apiService.getAvailableMembersNotWokenUp()
        setAvailableCount(availableData)
        console.log('Available count updated after requesting next number')
      } catch (countErr) {
        console.error('Failed to refresh available count:', countErr)
      }
    } finally {
      setNumberLoading(false)
      setIsLoadingNumber(false)
    }
  }

  const makeCall = (phoneNumber) => {
    window.open(`tel:${phoneNumber}`, '_self')
  }

  const recordWakeUp = async (wokeByPhone = null) => {
    try {
      await apiService.recordWakeUp(wokeByPhone)
      console.log('Wake up recorded successfully')
    } catch (err) {
      console.error('Failed to record wake up:', err)
      setError('Failed to record wake up')
    }
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading wake-up service...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>Error:</strong> {error}
            </div>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ProfileCompletionGuard>
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />

      {/* OTP Verification Warning */}
      {user && !user.otp_verified && (
        <div className="container mx-auto px-4 mt-4">
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start sm:items-center">
                <Icon name="AlertCircle" className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1 sm:mt-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base">নাম্বার ভেরিফিকেশন প্রয়োজন</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">সকল সেবা পেতে আপনার ফোন নাম্বার ভেরিফাই করুন</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/verify-otp')}
                className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-sm sm:text-base"
              >
                <Icon name="Phone" className="w-4 h-4 mr-2" />
                নাম্বার ভেরিফাই করুন
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card>
            <Card.Header className="bg-gradient-primary text-white rounded-t-lg p-6">
              {/* Centered Title */}
              <div className="text-center mb-4">
                <h1 className="text-2xl font-bold">জাগিয়ে দিন</h1>
              </div>

              {/* Time Information Row */}
              <div className="text-center text-sm mb-6">
                <span>সময় সীমা {timeSettings?.service_hours ? 
                  `${timeSettings.service_hours.start_time} - ${timeSettings.service_hours.end_time}` : 
                  '04:30 - 18:00'
                } / {formatBengaliTime(currentTime.toLocaleTimeString('en-US', { hour12: true }))}</span>
              </div>

              {/* Statistics Row - Responsive Design */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Stat 1: People Not Woken Up */}
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <div className="text-sm">
                    <span className="text-lg font-bold">{availableCount?.available_not_woken_up || 0}</span>
                    <span className="ml-1 opacity-80">জন এখনো জাগেনি</span>
                  </div>
                </div>
                
                {/* Stat 2: People Not Called */}
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <div className="text-sm">
                    <span className="text-lg font-bold">{availableCount?.available_not_called || 0}</span>
                    <span className="ml-1 opacity-80">জন এখনো কল পায়নি</span>
                  </div>
                </div>
                
                {/* Stat 3: Your Remaining Calls */}
                <div className="bg-white/20 rounded-lg p-3 text-center border border-white/20">
                  <div className="text-sm">
                    <span className="text-lg font-bold">{remainingCalls}/{maxCalls}</span>
                    <span className="ml-1 opacity-80">আপনার বাকী কল</span>
                  </div>
                </div>
              </div>
            </Card.Header>

            <Card.Content className="p-8">
              {!isServiceActive ? (
                <div className="text-center py-12">
                  <Icon name="Clock" className="w-16 h-16 mx-auto mb-6 text-orange-500" />
                  <h3 className="text-xl font-semibold mb-4 text-foreground">
                    ফজরের সময় নয়
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                    {timeSettings?.messages?.bengali || 'দয়া করে ফজরের সময় মানুষদের জাগান। নাম্বার ফজরের সময় পাওয়া যাবে।'}
                  </p>
                  {timeSettings?.service_hours && (
                    <div className="bg-accent/20 rounded-lg p-4 max-w-sm mx-auto">
                      <p className="text-sm text-muted-foreground mb-2">পরবর্তী সেবার সময়:</p>
                      <p className="font-semibold text-foreground">
                        {timeSettings.service_hours.start_time} - {timeSettings.service_hours.end_time}
                      </p>
                    </div>
                  )}
                </div>
              ) : !user.otp_verified ? (
                <div className="text-center py-12">
                  <Icon name="AlertCircle" className="w-16 h-16 mx-auto mb-6 text-yellow-500" />
                  <h3 className="text-xl font-semibold mb-4 text-foreground">
                    নাম্বার ভেরিফিকেশন প্রয়োজন
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                    ওয়েক আপ সেবা ব্যবহার করার জন্য প্রথমে আপনার ফোন নাম্বার ভেরিফাই করুন।
                    নাম্বার ভেরিফাই করার পর আপনি অন্যদের জাগানোর জন্য নাম্বার পাবেন।
                  </p>
                  <Button
                    onClick={() => navigate('/verify-otp')}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                    <Icon name="Phone" className="w-4 h-4 mr-2" />
                    নাম্বার ভেরিফাই করুন
                  </Button>
                </div>
              ) : (
                <WakeUpNumberCard
                  number={currentNumber}
                  trackingId={trackingId}
                  remainingCalls={remainingCalls}
                  maxCalls={maxCalls}
                  onStatusUpdate={handleStatusUpdate}
                  onRequestNext={handleRequestNext}
                  canRequestNext={canRequestNext}
                  loading={numberLoading}
                  showUpdateButton={showUpdateButton}
                  rateLimited={isRateLimited}
                  errorMessage={errorMessage}
                  errorReason={errorReason}
                  region={region}
                  availableCount={availableCount?.available_not_woken_up || 0}
                  rateLimitWaitTime={rateLimitWaitTime}
                />
              )}
            </Card.Content>
          </Card>
          
          {/* Community Wake Up Option */}
          <div className="mt-8 text-center">
            <Card className="bg-gradient-to-r from-green-50 to-teal-50 border-green-200">
              <Card.Content className="p-6">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <Icon name="Heart" className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">কমিউনিটি জাগান</h3>
                </div>
                <p className="text-green-700 mb-4 text-sm">
                  আপনার পরিবার, বন্ধু বা পরিচিতদের প্রাধান্য দিয়ে জাগান
                </p>
                <Button
                  onClick={() => window.location.href = '/community-wake-up'}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Icon name="Users" className="w-4 h-4 mr-2" />
                  কমিউনিটি জাগানোর জন্য যান
                </Button>
              </Card.Content>
            </Card>
          </div>
        </div>

        <div className="mt-12 max-w-4xl mx-auto">
          <div className="bg-accent/20 rounded-lg p-8">
            <h3 className="text-xl font-bold text-center mb-6">কীভাবে কল করবেন?</h3>
            
            {/* Important Note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex">
                <Icon name="AlertTriangle" className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-800 mb-1">গুরুত্বপূর্ণ নির্দেশনা:</p>
                  <p className="text-yellow-700 text-sm">প্রথমে অফলাইনে কল করুন, সর্বোচ্চ ২ বার কল করার চেষ্টা করুন। কল শেষে স্ট্যাটাস আপডেট করুন, তারপর পরবর্তী নম্বরের জন্য চাপুন।</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { 
                  num: 1, 
                  title: "অফলাইনে ২ বার কল করুন", 
                  desc: "প্রদর্শিত নম্বরে অফলাইনে কল করুন। সর্বোচ্চ ২ বার কল করার চেষ্টা করুন।" 
                },
                { 
                  num: 2, 
                  title: "উত্তর পেলে বলুন", 
                  desc: 'কল রিসিভ হলে বলুন: "ফজর ফাইটার থেকে বলছি, সালাতের জন্য জেগে উঠুন।"' 
                },
                { 
                  num: 3, 
                  title: "স্ট্যাটাস আপডেট করুন", 
                  desc: "কল শেষে অবশ্যই স্ট্যাটাস আপডেট করুন (কল শেষে কী ঘটেছে)।" 
                },
                { 
                  num: 4, 
                  title: "পরবর্তী নম্বর চান", 
                  desc: "স্ট্যাটাস আপডেটের পর 'পরবর্তী নম্বর' বাটনে চাপ দিন।" 
                }
              ].map((step, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {step.num}
                  </div>
                  <div>
                    <div className="font-medium">{step.title}</div>
                    <div className="text-sm text-muted-foreground">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="bg-gradient-to-r from-blue-50 to-green-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                📊 সংখ্যাগুলোর অর্থ বুঝুন
              </h2>
              <p className="text-lg text-gray-600">
                আপনি যে তথ্যগুলো দেখছেন, সেগুলো কীভাবে কাজ করে তা জানুন
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">😴</span>
                    "X জন এখনো জাগেনি"
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    এই সংখ্যাটি দেখায় যে আজকে কতজন ব্যবহারকারী এখনও ফজরের জন্য জেগে ওঠেননি। 
                    এরা হলেন সেই সব মুসলিম ভাই-বোন যাদের এখনও সাহায্যের প্রয়োজন। 
                    আপনি তাদের কল করে জাগিয়ে দিতে পারেন।
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">📞</span>
                    "X জন এখনো কল পায়নি"
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    এই সংখ্যাটি দেখায় যে কতজন ব্যবহারকারী আজকে এখনও কোনো সাহায্যকারী কল পাননি। 
                    এই তথ্যের মাধ্যমে আমরা বুঝতে পারি যে কোন ব্যবহারকারীদের সবচেয়ে বেশি সাহায্যের প্রয়োজন।
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">✅</span>
                    "X জন জেগে গেছেন"
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    এই সংখ্যাটি দেখায় যে আজকে কতজন ব্যবহারকারী সফলভাবে ফজরের জন্য জেগে উঠেছেন। 
                    এটি আমাদের সফলতার হার দেখায় এবং কমিউনিটির কার্যকারিতা পরিমাপ করে।
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">🔢</span>
                    "X/Y আপনার বাকী কল"
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    এটি দেখায় আজকের জন্য আপনার কতটি কল বাকি আছে। 
                    <strong>X</strong> হলো আপনি ইতিমধ্যে যতটি কল করেছেন, 
                    <strong>Y</strong> হলো আজকের জন্য আপনার সর্বোচ্চ কল সীমা। 
                    যখন X = Y হবে, তখন আজকের জন্য আপনার কল শেষ।
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">⏰</span>
                    কল সীমা কেন আছে?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    দৈনিক কল সীমা রয়েছে যাতে সবাই সমান সুযোগ পান এবং কেউ অতিরিক্ত বিরক্ত না হন। 
                    এটি আমাদের সিস্টেমকে ন্যায্য এবং কার্যকর রাখে। 
                    <strong>কমিউনিটি কল</strong> এই সীমার মধ্যে গণনা হয় না।
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-pink-500">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">🏆</span>
                    পয়েন্ট সিস্টেম
                  </h3>
                  <div className="text-gray-600 space-y-2">
                    <p>• কল করলে: <strong>২ পয়েন্ট</strong></p>
                    <p>• কল পৌঁছালে: <strong>অতিরিক্ত ৮ পয়েন্ট</strong> (মোট ১০)</p>
                    <p>• জেগে উঠলে: <strong>৫ পয়েন্ট</strong></p>
                    <p className="text-sm mt-3 bg-yellow-100 p-2 rounded">
                      💡 <strong>মনে রাখবেন:</strong> পয়েন্ট অর্জনের চেয়েও বড় কথা হলো আল্লাহর সন্তুষ্টি অর্জন!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center mt-12 bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                🤲 একসাথে গড়ে তুলি ফজর জাগরণের অভ্যাস
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                প্রতিটি কল, প্রতিটি সাহায্য হলো একটি সাদাকায়ে জারিয়া। 
                আপনার একটি কলই হতে পারে কারো জীবন পরিবর্তনের কারণ। 
                চলুন একসাথে মুসলিম উম্মাহকে ফজরের নামাজে জাগিয়ে তুলি! 🌅
              </p>
            </div>
          </div>
        </div>
      </div>

        <Footer />
      </div>
    </ProfileCompletionGuard>
  )
}

export default WakeUp